import { useMemo, useState } from "react";
import { Alert, Button, Nav, Spinner, Tab } from "react-bootstrap";

import {
  useGetAllCategories,
  useGetCategoriesWithConsentForms,
  useUploadCategoryConsentMutation,
} from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  useGetActiveRally,
  useGetRallies,
  useGetRallyById,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import {
  useGetRegistrationById,
  useGetRegistrationsByEvent,
  useUpdateCompetitorApprovalMutation,
} from "../../../../../api/jeep-rally/registrations/registrations.hooks";
import {
  isJeepRallyAdmin,
  normalizeCategories,
} from "../../Categories/utils/category.utils";
import {
  normalizeRallies,
  normalizeRally,
} from "../../Rallies/utils/rally.utils";
import CompetitorsFilters from "./_components/CompetitorsFilters";
import CompetitorsTable from "./_components/CompetitorsTable";
import RegistrationDetailModal from "./_components/RegistrationDetailModal";
import UploadConsentModal from "./_components/UploadConsentModal";
import {
  downloadConsentFormsForRegistrations,
  downloadPersonalizedConsent,
  getInheritedConsentInfo,
  getRegistrationApiErrorMessage,
  normalizeActiveRally,
  normalizeRegistration,
  normalizeRegistrations,
  openOrDownloadFile,
} from "./utils/competitors.utils";

const TAB_NEW = "new";
const TAB_PREVIOUS = "previous";

const CompetitorsListPage = () => {
  const canManage = isJeepRallyAdmin();
  const [activeTab, setActiveTab] = useState(TAB_NEW);

  const [newCategoryId, setNewCategoryId] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const [previousRallyId, setPreviousRallyId] = useState("");
  const [previousCategoryId, setPreviousCategoryId] = useState("");
  const [previousStatus, setPreviousStatus] = useState("");

  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [detailId, setDetailId] = useState("");
  const [detailFallback, setDetailFallback] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [approvingId, setApprovingId] = useState("");

  const {
    data: activeRallyData,
    isLoading: isActiveRallyLoading,
    isError: isActiveRallyError,
    error: activeRallyError,
  } = useGetActiveRally({
    enabled: activeTab === TAB_NEW,
  });

  const activeRally = useMemo(
    () =>
      normalizeActiveRally(activeRallyData) || normalizeRally(activeRallyData),
    [activeRallyData]
  );
  const activeEventId = activeRally?._id || activeRally?.id || "";

  const {
    data: ralliesData,
    isLoading: isRalliesLoading,
    isError: isRalliesError,
    error: ralliesError,
  } = useGetRallies({}, { enabled: activeTab === TAB_PREVIOUS });

  const rallies = useMemo(() => normalizeRallies(ralliesData), [ralliesData]);

  const { data: previousRallyData } = useGetRallyById(previousRallyId);
  const previousRally = useMemo(
    () => normalizeRally(previousRallyData),
    [previousRallyData]
  );

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetAllCategories();
  const { data: signedCategoriesData } = useGetCategoriesWithConsentForms();

  const categories = useMemo(() => {
    const all = normalizeCategories(categoriesData);
    const signed = normalizeCategories(signedCategoriesData);
    if (!signed.length) return all;

    const byId = new Map();
    all.forEach((item) => {
      const id = item._id || item.id;
      if (id) byId.set(id, item);
    });
    signed.forEach((item) => {
      const id = item._id || item.id;
      if (!id) return;
      byId.set(id, { ...(byId.get(id) || {}), ...item });
    });
    return Array.from(byId.values());
  }, [categoriesData, signedCategoriesData]);

  const newParams = useMemo(() => {
    const params = {};
    if (newCategoryId) params.category_id = newCategoryId;
    if (newStatus) params.status = newStatus;
    return params;
  }, [newCategoryId, newStatus]);

  const previousParams = useMemo(() => {
    const params = {};
    if (previousCategoryId) params.category_id = previousCategoryId;
    if (previousStatus) params.status = previousStatus;
    return params;
  }, [previousCategoryId, previousStatus]);

  const {
    data: newRegistrationsData,
    isLoading: isNewLoading,
    isFetching: isNewFetching,
    isError: isNewError,
    error: newError,
  } = useGetRegistrationsByEvent(activeEventId, newParams, {
    enabled: activeTab === TAB_NEW && Boolean(activeEventId),
  });

  const {
    data: previousRegistrationsData,
    isLoading: isPreviousLoading,
    isFetching: isPreviousFetching,
    isError: isPreviousError,
    error: previousError,
  } = useGetRegistrationsByEvent(previousRallyId, previousParams, {
    enabled: activeTab === TAB_PREVIOUS && Boolean(previousRallyId),
  });

  const newRegistrations = useMemo(
    () => normalizeRegistrations(newRegistrationsData),
    [newRegistrationsData]
  );
  const previousRegistrations = useMemo(
    () => normalizeRegistrations(previousRegistrationsData),
    [previousRegistrationsData]
  );

  const activeRegistrations =
    activeTab === TAB_NEW ? newRegistrations : previousRegistrations;

  const {
    data: detailData,
    isLoading: isDetailLoading,
  } = useGetRegistrationById(detailId, {
    enabled: Boolean(detailId),
  });

  const detailRegistration =
    normalizeRegistration(detailData) || detailFallback;

  const {
    mutate: updateApproval,
    isLoading: isApproving,
    isPending: isApprovePending,
  } = useUpdateCompetitorApprovalMutation();

  const {
    mutate: uploadConsent,
    isLoading: isUploadingConsent,
    isPending: isUploadPending,
  } = useUploadCategoryConsentMutation();

  const approving = isApproving || isApprovePending;
  const uploadingConsent = isUploadingConsent || isUploadPending;
  const uploadingCategoryId = uploadTarget
    ? getInheritedConsentInfo(uploadTarget, categories).categoryId
    : "";

  const handleDownloadFile = (url) => {
    if (!url) return;
    openOrDownloadFile(url);
  };

  const handleDownloadConsent = async (registration) => {
    try {
      const result = await downloadPersonalizedConsent(
        registration,
        categories
      );
      if (!result.ok) {
        const message =
          result.reason === "no_driver_name"
            ? "Driver name is missing for this registration."
            : result.reason === "generate_failed"
              ? "Failed to generate consent PDF."
              : "No consent template for this category.";
        setFeedback({ type: "warning", message });
        return;
      }
      setFeedback({
        type: "success",
        message: "Personalized consent PDF downloaded.",
      });
    } catch (error) {
      setFeedback({
        type: "danger",
        message: getRegistrationApiErrorMessage(
          error,
          "Failed to generate consent PDF."
        ),
      });
    }
  };

  const handleView = (registration) => {
    const id = registration?._id || registration?.id;
    if (!id) return;
    setDetailFallback(registration);
    setDetailId(id);
  };

  const closeDetail = () => {
    setDetailId("");
    setDetailFallback(null);
  };

  const handleApproval = (registration, status) => {
    const registrationId = registration?._id || registration?.id;
    if (!registrationId || !canManage) return;

    setFeedback({ type: "", message: "" });
    setApprovingId(registrationId);

    updateApproval(
      { registrationId, status },
      {
        onSuccess: () => {
          setFeedback({
            type: "success",
            message:
              status === "approved"
                ? "Registration approved."
                : "Registration set back to pending.",
          });
          setApprovingId("");
          if (detailId === registrationId) {
            setDetailFallback((prev) =>
              prev ? { ...prev, status } : prev
            );
          }
        },
        onError: (error) => {
          setFeedback({
            type: "danger",
            message: getRegistrationApiErrorMessage(
              error,
              "Failed to update registration approval."
            ),
          });
          setApprovingId("");
        },
      }
    );
  };

  const handleOpenUpload = (registration) => {
    setUploadError("");
    setUploadTarget(registration);
  };

  const handleCloseUpload = () => {
    if (uploadingConsent) return;
    setUploadTarget(null);
    setUploadError("");
  };

  const handleUploadConsent = ({ categoryId, file }) => {
    if (!canManage || !categoryId || !file) return;

    setUploadError("");
    uploadConsent(
      { categoryId, file },
      {
        onSuccess: () => {
          setFeedback({
            type: "success",
            message:
              "Signed consent uploaded. All registrations in this category now show as signed.",
          });
          setUploadTarget(null);
        },
        onError: (error) => {
          setUploadError(
            getRegistrationApiErrorMessage(
              error,
              "Failed to upload signed consent."
            )
          );
        },
      }
    );
  };

  const handleToggleSelect = (id) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(
      activeRegistrations
        .map((item) => item._id || item.id)
        .filter(Boolean)
    );
  };

  const handleBulkDownload = async () => {
    const selected = activeRegistrations.filter((item) =>
      selectedIds.includes(item._id || item.id)
    );

    if (!selected.length) {
      setFeedback({
        type: "warning",
        message: "Select at least one registration to download consent forms.",
      });
      return;
    }

    try {
      const result = await downloadConsentFormsForRegistrations(
        selected,
        categories
      );

      const skipParts = [];
      if (result.skippedNoTemplate) {
        skipParts.push(
          `${result.skippedNoTemplate} no template`
        );
      }
      if (result.skippedNoDriver) {
        skipParts.push(
          `${result.skippedNoDriver} missing driver name`
        );
      }
      const otherSkipped =
        result.skipped -
        result.skippedNoTemplate -
        result.skippedNoDriver;
      if (otherSkipped > 0) {
        skipParts.push(`${otherSkipped} failed`);
      }

      setFeedback({
        type: result.downloaded ? "success" : "warning",
        message: `Downloaded ${result.downloaded} consent form${
          result.downloaded === 1 ? "" : "s"
        }${
          result.skipped
            ? `, skipped ${result.skipped} (${skipParts.join(", ")})`
            : ""
        }.`,
      });
    } catch (error) {
      setFeedback({
        type: "danger",
        message: getRegistrationApiErrorMessage(
          error,
          "Failed to download consent forms."
        ),
      });
    }
  };

  const handlePreviousRallyChange = (rallyId) => {
    setPreviousRallyId(rallyId);
    setPreviousCategoryId("");
    setPreviousStatus("");
    setSelectedIds([]);
  };

  const handleTabChange = (key) => {
    if (!key) return;
    setActiveTab(key);
    setSelectedIds([]);
  };

  const renderRegistrationsPanel = ({
    eventId,
    rallyName,
    showRallySelect,
    selectedRallyId,
    onRallyChange,
    selectedCategoryId,
    onCategoryChange,
    selectedStatus,
    onStatusChange,
    registrations,
    isLoading,
    isFetching,
    isError,
    error,
    emptyMessage,
    contextLoading,
    contextError,
    contextErrorMessage,
  }) => {
    if (contextLoading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      );
    }

    if (contextError) {
      return (
        <div className="text-danger py-4">
          {contextErrorMessage ||
            getRegistrationApiErrorMessage(error, "Something went wrong.")}
        </div>
      );
    }

    return (
      <>
        {rallyName ? (
          <p className="text-muted mb-3">
            Rally: <strong>{rallyName}</strong>
          </p>
        ) : null}

        <CompetitorsFilters
          showRallySelect={showRallySelect}
          rallies={rallies}
          isRalliesLoading={isRalliesLoading}
          selectedRallyId={selectedRallyId}
          onRallyChange={onRallyChange}
          categories={categories}
          isCategoriesLoading={isCategoriesLoading}
          selectedCategoryId={selectedCategoryId}
          onCategoryChange={(value) => {
            onCategoryChange(value);
            setSelectedIds([]);
          }}
          selectedStatus={selectedStatus}
          onStatusChange={(value) => {
            onStatusChange(value);
            setSelectedIds([]);
          }}
        />

        {eventId && registrations.length > 0 ? (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              disabled={selectedIds.length === 0}
              onClick={handleBulkDownload}
            >
              Download consent forms
              {selectedIds.length ? ` (${selectedIds.length})` : ""}
            </Button>
            <span className="text-muted small">
              Downloads one personalized consent PDF per selected driver
              (category template + driver name + signature block).
            </span>
          </div>
        ) : null}

        {!eventId ? (
          <div className="text-center text-muted py-5">
            <p className="mb-0">{emptyMessage}</p>
          </div>
        ) : isLoading || isFetching ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : isError ? (
          <div className="text-danger py-4">
            Failed to load registrations.{" "}
            {getRegistrationApiErrorMessage(error, "Please try again.")}
          </div>
        ) : registrations.length === 0 ? (
          <div className="text-center text-muted py-5">
            <p className="mb-0">No registrations found for this rally.</p>
          </div>
        ) : (
          <CompetitorsTable
            registrations={registrations}
            categories={categories}
            canManage={canManage}
            selectedIds={selectedIds}
            approvingId={approving ? approvingId : ""}
            uploadingCategoryId={
              uploadingConsent ? uploadingCategoryId : ""
            }
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            onApprove={(item) => handleApproval(item, "approved")}
            onUnapprove={(item) => handleApproval(item, "pending")}
            onView={handleView}
            onDownloadConsent={handleDownloadConsent}
            onDownloadSignedConsent={handleDownloadFile}
            onUploadSignedConsent={handleOpenUpload}
          />
        )}
      </>
    );
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Competitors</h4>
        </div>

        <div className="card-body">
          {!canManage && (
            <Alert variant="warning" className="mb-3">
              Admin access is required to approve registrations or upload signed
              consent.
            </Alert>
          )}

          {feedback.message && (
            <Alert
              variant={feedback.type || "info"}
              className="mb-3"
              dismissible
              onClose={() => setFeedback({ type: "", message: "" })}
            >
              {feedback.message}
            </Alert>
          )}

          <Tab.Container
            activeKey={activeTab}
            onSelect={handleTabChange}
          >
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link eventKey={TAB_NEW}>Active rally</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_PREVIOUS}>Previous rallies</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey={TAB_NEW}>
                {renderRegistrationsPanel({
                  eventId: activeEventId,
                  rallyName: activeRally?.name || "",
                  showRallySelect: false,
                  selectedRallyId: activeEventId,
                  onRallyChange: () => {},
                  selectedCategoryId: newCategoryId,
                  onCategoryChange: setNewCategoryId,
                  selectedStatus: newStatus,
                  onStatusChange: setNewStatus,
                  registrations: newRegistrations,
                  isLoading: isNewLoading,
                  isFetching: isNewFetching,
                  isError: isNewError,
                  error: newError,
                  emptyMessage: "No active rally found.",
                  contextLoading: isActiveRallyLoading,
                  contextError: isActiveRallyError,
                  contextErrorMessage: `Failed to load active rally. ${getRegistrationApiErrorMessage(
                    activeRallyError,
                    "Please try again."
                  )}`,
                })}
              </Tab.Pane>

              <Tab.Pane eventKey={TAB_PREVIOUS}>
                {renderRegistrationsPanel({
                  eventId: previousRallyId,
                  rallyName:
                    previousRally?.name ||
                    rallies.find(
                      (item) => (item._id || item.id) === previousRallyId
                    )?.name ||
                    "",
                  showRallySelect: true,
                  selectedRallyId: previousRallyId,
                  onRallyChange: handlePreviousRallyChange,
                  selectedCategoryId: previousCategoryId,
                  onCategoryChange: setPreviousCategoryId,
                  selectedStatus: previousStatus,
                  onStatusChange: setPreviousStatus,
                  registrations: previousRegistrations,
                  isLoading: isPreviousLoading,
                  isFetching: isPreviousFetching,
                  isError: isPreviousError,
                  error: previousError,
                  emptyMessage: "Select a rally to view registrations.",
                  contextLoading: false,
                  contextError: isRalliesError,
                  contextErrorMessage: `Failed to load rallies. ${getRegistrationApiErrorMessage(
                    ralliesError,
                    "Please try again."
                  )}`,
                })}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>

      <RegistrationDetailModal
        show={Boolean(detailId)}
        registration={detailRegistration}
        categories={categories}
        isLoading={isDetailLoading}
        canManage={canManage}
        approving={approving && approvingId === detailId}
        uploading={
          uploadingConsent &&
          getInheritedConsentInfo(detailRegistration, categories).categoryId ===
            uploadingCategoryId
        }
        onClose={closeDetail}
        onApprove={(item) => handleApproval(item, "approved")}
        onUnapprove={(item) => handleApproval(item, "pending")}
        onDownloadConsent={handleDownloadConsent}
        onDownloadSignedConsent={handleDownloadFile}
        onUploadSignedConsent={handleOpenUpload}
      />

      <UploadConsentModal
        show={Boolean(uploadTarget)}
        registration={uploadTarget}
        categories={categories}
        uploading={uploadingConsent}
        error={uploadError}
        onClose={handleCloseUpload}
        onSubmit={handleUploadConsent}
      />
    </div>
  );
};

export default CompetitorsListPage;
